import React, { useState, useEffect } from 'react';
import { getPublicPartners } from '../services/api';

const DynamicPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectionContent, setSectionContent] = useState(null);

  useEffect(() => {
    fetchPartners();
    fetchSectionContent();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await getPublicPartners();
      console.log('Partners API Response:', response);
      
      // Extract partners data from response
      let partnersData = [];
      if (response && response.data && Array.isArray(response.data)) {
        partnersData = response.data;
      } else if (response && Array.isArray(response)) {
        partnersData = response;
      } else if (response && response.partners && Array.isArray(response.partners)) {
        partnersData = response.partners;
      }
      
      // Filter active partners and sort by order
      const activePartners = partnersData
        .filter(p => p.isActive !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      
      setPartners(activePartners);
      console.log('Partners loaded:', activePartners.length);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSectionContent = async () => {
    try {
      const response = await fetch('https://inteliheads.in/api/cms/public/content/partners');
      const data = await response.json();
      if (data && data.data) {
        setSectionContent(data.data);
      } else if (data && data._id) {
        setSectionContent(data);
      }
    } catch (error) {
      console.error('Error fetching section content:', error);
    }
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  const getPartnerIcon = (partnerName) => {
    const name = partnerName.toLowerCase();
    const icons = {
      'digsilent': '⚡',
      'ansys': '🔬',
      'mathworks': '📈',
      'matlab': '📈',
      'nvidia': '🤖',
      'aws': '☁️',
      'aicte': '🏛️',
      'microsoft': '💻',
      'google': '🔍',
      'ibm': '💾',
      'oracle': '🗄️',
      'salesforce': '☁️',
      'sap': '📊',
      'adobe': '🎨',
      'cisco': '🌐'
    };
    for (const [key, icon] of Object.entries(icons)) {
      if (name.includes(key)) return icon;
    }
    return '🤝';
  };

  if (loading) {
    return (
      <section id="partners" style={{ padding: '5rem 0', background: '#F7F9FC' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#2C7A7B' }}></i>
          <p style={{ marginTop: '1rem', color: '#5A6E7F' }}>Loading partners...</p>
        </div>
      </section>
    );
  }

  const title = sectionContent?.title || 'Our Global Partners';
  const subtitle = sectionContent?.subtitle || 'Collaborating with industry leaders to deliver excellence worldwide.';
  const backgroundColor = sectionContent?.backgroundColor || '#F7F9FC';
  const description = sectionContent?.content || '';

  return (
    <section id="partners" style={{
      padding: 'clamp(3rem, 8vw, 5rem) 0',
      background: backgroundColor
    }}>
      <div className="container">
        {/* Header Section */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 style={{
            fontSize: 'clamp(1.8rem, 5vw, 2.5rem)',
            marginBottom: '1rem',
            color: '#0A192F',
            fontWeight: 700
          }}>
            {title}
          </h2>
          
          <div style={{
            width: '60px',
            height: '4px',
            background: '#2C7A7B',
            margin: '0 auto 1rem',
            borderRadius: '2px'
          }} />
          
          <p style={{
            fontSize: 'clamp(1rem, 4vw, 1.125rem)',
            color: '#5A6E7F',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>
            {subtitle}
          </p>
          
          {description && (
            <p style={{
              fontSize: '0.95rem',
              color: '#5A6E7F',
              maxWidth: '600px',
              margin: '1rem auto 0',
              lineHeight: '1.6'
            }}>
              {description}
            </p>
          )}
        </div>

        {partners.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem',
            background: 'white',
            borderRadius: '20px',
            border: '2px dashed #E2E8F0'
          }}>
            <i className="fas fa-handshake" style={{ fontSize: '4rem', color: '#CBD5E1', marginBottom: '1rem' }}></i>
            <p style={{ color: '#5A6E7F', fontSize: '1.1rem' }}>No partners available at the moment.</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: '2rem'
          }}>
            {partners.map((partner, index) => (
              <div
                key={partner._id || index}
                data-aos="fade-up"
                data-aos-delay={index * 50}
                style={{
                  background: 'white',
                  padding: '2rem',
                  borderRadius: '16px',
                  textAlign: 'center',
                  boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)',
                  border: '1px solid #E2E8F0',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 30px -12px rgba(0,0,0,0.15)';
                  e.currentTarget.style.borderColor = '#2C7A7B';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(0,0,0,0.05)';
                  e.currentTarget.style.borderColor = '#E2E8F0';
                }}
              >
                {/* Partner Logo/Icon */}
                {partner.logo ? (
                  <div style={{
                    width: '100px',
                    height: '100px',
                    margin: '0 auto 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <img
                      src={getFullImageUrl(partner.logo)}
                      alt={partner.name}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        transition: 'transform 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `<div style="font-size: 3rem;">${getPartnerIcon(partner.name)}</div>`;
                      }}
                    />
                  </div>
                ) : (
                  <div style={{
                    fontSize: '3rem',
                    marginBottom: '1rem'
                  }}>
                    {partner.icon || getPartnerIcon(partner.name)}
                  </div>
                )}

                {/* Partner Type Badge */}
                {partner.type && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <span style={{
                      background: '#E3F2FD',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.7rem',
                      color: '#2C7A7B',
                      fontWeight: 600,
                      display: 'inline-block'
                    }}>
                      {partner.type}
                    </span>
                  </div>
                )}

                {/* Partner Name */}
                <h4 style={{
                  fontSize: '1.2rem',
                  marginBottom: '0.5rem',
                  color: '#0A192F',
                  fontWeight: 700
                }}>
                  {partner.name}
                </h4>

                {/* Partner Description */}
                <p style={{
                  fontSize: '0.85rem',
                  color: '#5A6E7F',
                  lineHeight: '1.5',
                  marginBottom: '1rem'
                }}>
                  {partner.description || 'Strategic Partner'}
                </p>

                {/* Website Link */}
                {partner.website && (
                  <a
                    href={partner.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.4rem 1rem',
                      background: 'transparent',
                      color: '#2C7A7B',
                      border: '1px solid #2C7A7B',
                      borderRadius: '40px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textDecoration: 'none',
                      transition: 'all 0.3s ease',
                      marginTop: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#2C7A7B';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#2C7A7B';
                    }}
                  >
                    Visit Website <i className="fas fa-external-link-alt"></i>
                  </a>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Call to Action */}
        <div style={{
          textAlign: 'center',
          marginTop: '4rem',
          padding: '2rem',
          background: 'linear-gradient(135deg, #0A192F 0%, #1E3A5F 100%)',
          borderRadius: '16px',
          color: 'white'
        }}>
          <h3 style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}>
            Become a Partner
          </h3>
          <p style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '1rem' }}>
            Join our network of industry-leading partners
          </p>
          <button
            onClick={() => {
              window.dispatchEvent(new CustomEvent('openContactModal'));
            }}
            style={{
              padding: '0.6rem 1.5rem',
              background: '#2C7A7B',
              color: 'white',
              border: 'none',
              borderRadius: '40px',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#236B6C'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#2C7A7B'}
          >
            Contact Us <i className="fas fa-arrow-right"></i>
          </button>
        </div>
      </div>
    </section>
  );
};

export default DynamicPartners;